import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SentenceStatus from "./components/SentenceStatus";
import ShuffledWordPanel from "./components/ShuffledWordPanel";
import ArrangedWordPanel from "./components/ArrangedWordPanel";
import ScoreCard from "./components/ScoreCard";
import { CheckCircle, XCircle, Home, Volume2, VolumeX } from "lucide-react";
import Timer from "./components/Timer";
import { motion } from "framer-motion"; // Importing motion for animation
import "./App.css";
import api from "./utils/api"; // Import the API utility
import { useAuth } from "./contexts/AuthProvider";

function Kuralattam1() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Add a state to track if score has been posted
  const [scorePosted, setScorePosted] = useState(false);
  const myChapters = location.state?.message || "nothing"; // Read values passed on state
  const [selectedKurals, setSelectedKurals] = useState([]); // State to hold the 10 random kurals
  const [currentKuralIndex, setCurrentKuralIndex] = useState(0); // To track which kural is being shown
  const [shuffledWords, setShuffledWords] = useState([]);
  const [completedSentences, setcompletedSentences] = useState([]);
  const [userAnswer, setUserAnswer] = useState([]);
  const [score, setScore] = useState({
    correctSentences: 0,
    correctWords: 0,
    startTime: null,
    endTime: null,
    gameid: null,
  });
  const [filterChapters, setFilterChapters] = useState(null); // State to store filter chapters
  const [gameStarted, setGameStarted] = useState(false); // Track if the game has started
  const [gameOver, setGameOver] = useState(false); // Track if the game is over
  const [sentenceStatus, setSentenceStatus] = useState([]); // New state to track sentence status
  const [timeLimit, setTimeLimit] = useState(5); // Configurable time limit (default is 5 minutes)
  const [remainingTime, setRemainingTime] = useState(timeLimit * 60); // Remaining time in seconds
  const [TestTitle, setTestTitle] = useState(""); // State to hold the 10 random kurals
  const [isMute, setIsMute] = useState(true);
  //----------------------------------------
  // Load sounds for correct and incorrect actions
  const correctSound = new Audio("/sounds/correct.mp3");
  const incorrectSound = new Audio("/sounds/incorrect.mp3");

  // Function to start a new game (show a new kural)
  const startNewGame = useCallback((kuralSet, index) => {
    if (index >= kuralSet.length) {
      setGameOver(true); // Mark the game as over if all sentences are completed
      return;
    }

    const selectedKural = kuralSet[index];
    const concatenatedKural = selectedKural.kural.join(" ");

    // Split the sentence into words and remove periods using .replace()
    const words = concatenatedKural
      .split(" ")
      .map((word) => word.replace(".", ""));

    setCurrentKuralIndex(index);
    setShuffledWords(shuffleArray([...words]));
    setUserAnswer([]);
  }, []);

  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5);
  };

  const handleWordClick = (word, index) => {
    // Add clicked word to the userAnswer array
    const updatedUserAnswer = [...userAnswer, word];
    setUserAnswer(updatedUserAnswer);

    console.log("filterChapters", filterChapters);
    // Remove the clicked word by replacing it with null
    setShuffledWords((prevWords) =>
      prevWords.map((w, i) => (i === index ? null : w))
    );

    if (selectedKurals.length && selectedKurals[currentKuralIndex]) {
      const correctWords = selectedKurals[currentKuralIndex].kural
        .join(" ")
        .split(" ")
        .map((word) => word.replace(".", ""));
      const isCorrectWord = correctWords[updatedUserAnswer.length - 1] === word;

      if (isCorrectWord) {
        if (!isMute) correctSound.play();
      } else {
        if (!isMute) incorrectSound.play();
      }

      // Check if all words are arranged, pass updatedUserAnswer to checkAnswer
      if (updatedUserAnswer.length === correctWords.length) {
        checkAnswer(updatedUserAnswer); // Pass the updatedUserAnswer directly
      }
    }
  };

  const checkAnswer = useCallback(
    (updatedUserAnswer) => {
      if (!selectedKurals.length || !selectedKurals[currentKuralIndex]) {
        return;
      }

      const correctWords = selectedKurals[currentKuralIndex].kural
        .join(" ")
        .split(" ")
        .map((word) => word.replace(".", ""));
      const correctKural =
        selectedKurals[currentKuralIndex].kural[0].replace(".", "") +
        " " +
        selectedKurals[currentKuralIndex].kural[1].replace(".", "");

      const isCorrectSentence =
        updatedUserAnswer.join(" ") === correctWords.join(" ");
      const completedKural = updatedUserAnswer.join(" ");

      setcompletedSentences([
        ...completedSentences,
        { sentence: completedKural, isCorrectSentence, correctKural },
      ]);
      console.log("completedSentences > ", completedSentences);
      console.log("isCorrectSentence > ", isCorrectSentence);

      let correctWordCount = 0;
      updatedUserAnswer.forEach((word, index) => {
        if (word === correctWords[index]) {
          correctWordCount++;
        }
      });

      // Update sentence status
      setSentenceStatus((prevStatus) => {
        const newStatus = [...prevStatus];
        newStatus[currentKuralIndex] = isCorrectSentence
          ? "correct"
          : "incorrect";
        return newStatus;
      });

      // Update the score
      setScore((prevScore) => ({
        ...prevScore,
        correctSentences:
          prevScore.correctSentences + (isCorrectSentence ? 1 : 0),
        correctWords: prevScore.correctWords + correctWordCount,
      }));

      // Move to the next sentence
      //startNewGame(selectedKurals, currentKuralIndex + 1);
      // Automatically move to the next Kural or end the game if all sentences are completed
      setTimeout(() => {
        startNewGame(selectedKurals, currentKuralIndex + 1); // Start the next Kural after a short delay
      }, 1000); // 1-second delay to let user see the result
    },
    [selectedKurals, currentKuralIndex, startNewGame, completedSentences]
  );

  // Function to post score to the API
  const postScore = useCallback(async () => {
    if (scorePosted) return; // Prevent duplicate submissions
    console.log("Posting score...", score);
    const mappedScoreDetails = completedSentences.map((item) => ({
      correct_kural: item.correctKural,
      your_arrangement: item.sentence,
      isCorrect: item.isCorrectSentence ? 1 : 0,
    }));
    try {
      const scoreData = {
        Name: user.name, // You might want to get this from user input
        MobileNo: user.mobileno, // You might want to get this from user input
        StartTime: score.startTime,
        EndTime: new Date().toISOString().slice(0, 19).replace("T", " "),
        CorrectWords: score.correctWords,
        GameID: score.gameid, // You might want to get the actual game ID
        CorrectSentence: score.correctSentences,
        TimeTaken: "00:10",
        ReminingTime: "9:10",
        ScoreDetails: mappedScoreDetails,
      };

      const response = await api.post("/api/score/", scoreData);
      console.log("Score posted successfully:", response.data);
      //console.log("Score Selected Kurals:", selectedKurals);
      console.log("Completed Kurals:", mappedScoreDetails);
      setScorePosted(true);
    } catch (error) {
      console.error("Error posting score:", error);
    }
  }, [scorePosted, score, user, completedSentences]);

  useEffect(() => {
    if (gameOver && !scorePosted) {
      postScore();
    }
  }, [gameOver, scorePosted, postScore]);

  useEffect(() => {
    if (gameStarted && !gameOver && remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else if (remainingTime === 0) {
      setGameOver(true);
    }
  }, [gameStarted, gameOver, remainingTime]);

  useEffect(() => {
    if (myChapters !== "nothing") {
      const no = location.state.number;
      const time = location.state.time;
      const titile = location.state.titile;
      const mydata = location.state.data;
      const gameid = location.state.gameid;
      console.log("no ", no);
      console.log("time ", time);
      console.log("titile ", titile);
      console.log("mydata ", mydata);
      console.log("gameid ", gameid);
      setSelectedKurals(mydata);
      setSentenceStatus(new Array(no).fill("unanswered"));
      startNewGame(mydata, 0);
      setFilterChapters(myChapters);
      setGameStarted(true);
      setGameOver(false);
      setRemainingTime(time * 60);
      setTimeLimit(time);
      setTestTitle(titile);
      setScore((prevScore) => ({
        ...prevScore,
        gameid: gameid,
      }));
    }
  }, [myChapters, location, startNewGame]);

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const calculateTimeTaken = () => {
    const totalTime = timeLimit * 60;
    const timeTaken = totalTime - remainingTime;
    return formatTime(timeTaken);
  };

  const handleRestartGame = () => {
    setGameStarted(false);
    setGameOver(false);
    setSelectedKurals([]);
    setCurrentKuralIndex(0);
    setSentenceStatus([]);
    setScore({
      correctSentences: 0,
      correctWords: 0,
      startTime: null,
      endTime: null,
    });
    setRemainingTime(timeLimit * 60);
    navigate("/");
  };

  const muteSound = () => {
    setIsMute((prev) => !prev);
  };

  const handleHome = () => {
    navigate("/aattam");
  };

  // Add this in the function where you start the game, likely in startNewGame or when gameStarted becomes true
  useEffect(() => {
    if (gameStarted && !gameOver) {
      console.log("Game started!");
      setScore((prevScore) => ({
        ...prevScore,
        startTime: new Date().toISOString().slice(0, 19).replace("T", " "),
      }));
    }
  }, [gameStarted, gameOver]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[60%_40%] md:grid-cols-[65%_35%] lg:grid-cols-[70%_30%] gap-3">
      <div className="bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 p-4 rounded relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white hidden lg:block">
            குறளாட்டம் ஒன்றாம் நிலை
          </h2>
          <div className="flex space-x-2">
            <button
              className="flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-gray-600"
              onClick={() => muteSound(!isMute)}
            >
              {isMute ? (
                <VolumeX size={24} className="sm:block" />
              ) : (
                <Volume2 size={24} className="sm:block" />
              )}
              <span className="hidden sm:inline">
                {isMute ? "Unmute" : "Mute"}
              </span>
            </button>
            <button
              onClick={handleHome}
              className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-600"
            >
              <Home size={24} className="sm:block" />
              <span className="hidden sm:inline">Home</span>
            </button>
          </div>
        </div>
        <div className=" w-full max-w-full  ">
          <div className="min-h-full">
            {/* Display the game only if it has started */}
            {gameStarted && !gameOver && (
              <>
                {/* Panel 1: Shuffled Words with Fixed Position and Removed Words */}
                <div className="rounded-lg border border-blue-300 bg-gradient-to-r from-blue-200 to-cyan-200 p-6 m-4  min-h-52">
                  <div className="mb-4 flex justify-between items-center relative w-full">
                    {/* Left-aligned content */}
                    <div className="text-left">
                      <h2 className="text-xl font-bold">கலைக்கப்பட்ட குறள்</h2>{" "}
                      <h2 className="text-sm text-gray-70">{TestTitle}</h2>
                      {/* Example Left Content */}
                    </div>
                    {/* Left-aligned content */}
                    <div className="text-right sm:block lg:hidden">
                      <Timer time={formatTime(remainingTime)} />
                    </div>
                    {/* Center-aligned Sentence Status Indicator and Time Left */}
                    <div className="absolute inset-x-0 top-0 flex flex-col items-center ">
                      {/* Time Left Display */}
                      <div className="hidden lg:block">
                        <Timer time={formatTime(remainingTime)} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex justify-between items-center ">
                    <ShuffledWordPanel
                      shuffledWords={shuffledWords}
                      handleWordClick={handleWordClick}
                    />
                  </div>
                  {/* Center-aligned Sentence Status Indicator and Time Left */}
                  <div className=" inset-x-0 top-0 flex flex-col items-center mt-3">
                    <SentenceStatus
                      sentenceStatus={sentenceStatus}
                      currentKuralIndex={currentKuralIndex}
                    />
                  </div>
                </div>
                {/* Panel 2: Arranged Words (User's Sentence) */}
                <div className="m-4  border border-indigo-700 min-h-60 p-6 bg-gradient-to-br from-blue-200 via-blue-400 to-blue-600 rounded-lg">
                  <h2 className="text-xl font-semibold mb-2">
                    அடுக்கப்பட்ட குறள்
                  </h2>
                  <ArrangedWordPanel
                    userAnswer={userAnswer}
                    selectedKurals={selectedKurals}
                    currentKuralIndex={currentKuralIndex}
                  />
                </div>
                {/* Panel 3: Score */}
              </>
            )}

            {/* Game Over and Restart */}
            {gameOver && (
              <div className="">
                <div className="text-center">
                  <h2 className="text-3xl text-yellow-200 font-semibold mb-4">
                    Game Over!
                  </h2>
                  <h2 className="text-2xl text-yellow-400 font-semibold mb-4">
                    Time's up or you've completed all sentences.
                  </h2>
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ rotate: 360, scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                  }}
                >
                  <ScoreCard
                    correctSentences={score.correctSentences}
                    correctWords={score.correctWords}
                    timeTaken={calculateTimeTaken()}
                    timeRemining={formatTime(remainingTime)}
                    handleRestart={handleRestartGame}
                  />
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-r rounded from-green-700 via-green-500 to-emerald-500 p-4 relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Score</h2>
        </div>
        <div className="space-y-4">
          <div className="border p-4 rounded bg-gray-100">
            <ul className="list-none m-0 p-0">
              <li className="py-2 border-b last:border-b-0 border-gray-200 text-lg text-stone-600 font-semibold ">
                சரியாக அடுக்கப்பட்ட குறள்கள் : {score.correctSentences}
              </li>
              <li className="py-2 border-b last:border-b-0 border-gray-200 text-lg text-stone-600 font-semibold">
                சரியாக அடுக்கப்பட்ட சீர்கள் : {score.correctWords}
              </li>
              <li className="py-2 border-b last:border-b-0 border-gray-200 text-lg text-stone-600 font-semibold">
                எடுத்துக்கொண்ட நேரம் : {calculateTimeTaken()}
              </li>
              <li className="py-2 border-b last:border-b-0 border-gray-200 text-lg text-stone-600 font-semibold">
                மீத நேரம்: {formatTime(remainingTime)}
              </li>
            </ul>
          </div>
          <div className="border p-4 rounded bg-gray-100">
            <h3 className="font-semibold">Completed Sentences</h3>
            <ul>
              {completedSentences.map((entry, index) => (
                <li
                  key={index}
                  className="flex items-center gap-4 p-2 border rounded-lg shadow-md"
                >
                  {entry.isCorrectSentence ? (
                    <CheckCircle className="text-green-500" size={20} />
                  ) : (
                    <XCircle className="text-red-500" size={20} />
                  )}
                  <p className="text-gray-700 whitespace-pre-line text-l font-bold">
                    {entry.sentence.split(" ").slice(0, 4).join(" ") +
                      "\n" +
                      entry.sentence.split(" ").slice(4).join(" ")}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Kuralattam1;
